import { Component, OnDestroy, OnInit, Input } from "@angular/core";
import { Subscription } from "rxjs";
import { FormField } from "src/app/interfaces/form-field.model";
import { faSignInAlt } from "@fortawesome/free-solid-svg-icons";
import { VehicleSelectionService } from "src/app/services/vehicle/vehice-selection.service";
import { ActivatedRoute, Router } from "@angular/router";
import { NotifierService } from "angular-notifier";

@Component({
  selector: "app-vehicle-selection",
  templateUrl: "./vehicle-selection.component.html",
  styleUrls: ["./vehicle-selection.component.css"],
})
export class VehicleSelectionComponent implements OnInit, OnDestroy {
  private stockRequest?: Subscription;
  faSignInAlt = faSignInAlt;

  @Input() standardSpecification!: FormField[];

  searchInput: string = "";

  models: any[] = [];
  selectedModelCode: string = '';
  selectedModelName: string = '';
  selectedColour: string = '';
  filteredData: any[] = [];
  colours: any[] = [];
  vehicleStock: any[] = [];
  sellingPrice: string = '';

  tableVisible: boolean = false;
  specVisible: boolean = false;

  tableHeaders_vehicleStock = this.generateVehicleStockHeaders();

  tableHeaders_standardAccessories = [
    { title: "ITEM", type: "data", id: "item" },
  ];

  standardAccessories: any[] = [];

  tableHeaders_optionalAccessories = [
    { title: "ITEM", type: "data", id: "item" },
    { title: "PRICE ($)", type: "data", id: "price" },
  ];

  optionalAccessories: any[] = [];

  upperFormFields: FormField[] = [
    {
      type: "select",
      id: "model",
      label: "Select Model",
      options: []
    },
    {
      type: "select",
      id: "right",
      label: "Select Colour",
      options: []
    },
    {
      type: "const",
      id: "menuName",
      label: "",
    },
  ];


  constructor(
    private vehicleSelectionService: VehicleSelectionService,
    private router: Router,
    private route: ActivatedRoute,
    private notifier: NotifierService,
  ) {}
  

  ngOnInit() {
    const navState = history.state;
    const initialModelCode: string = navState?.modelCode || '';
    const initialColourCode: string = navState?.colourCode || '';

    this.vehicleSelectionService.getModelActiveInSalesDrpList().subscribe(
      (response) => {
        if (Array.isArray(response?.body)) {
          this.models = response.body.map((model: any) => ({
            value: model.Value,
            display: model.Name,
          }));

          if (initialModelCode) {
            this.selectedModelCode = initialModelCode;
            const selectedModel = this.models.find((m: any) => m.value === initialModelCode);
            this.selectedModelName = selectedModel ? selectedModel.display : '';
            this.getVehicleStockAvailability(this.selectedModelName);
            this.getColoursByModel(this.selectedModelCode, initialColourCode);
          }

          this.updateUpperFormFields();
        } else {
          this.notifier.notify('error', response?.message || 'Error loading vehicle models.');
        }
      },
      (error) => {
        console.error("Error getting models:", error);
        this.notifier.notify('error', 'Error loading vehicle models.');
      }
    );

    this.standardSpecification = [];
  }

  updateUpperFormFields(): void {
    const selectedModel = this.selectedModelCode;
    const selectedColour = this.selectedColour;
  
    this.upperFormFields = [
      {
        type: "select",
        id: "model",
        label: "Select Model",
        options: this.models,
        value: selectedModel
      },
      {
        type: "select",
        id: "right",
        label: "Select Colour",
        options: this.colours,
        value: selectedColour
      },
      {
        type: "const",
        id: "menuName",
        label: this.specVisible ? "Selling Price" : "",
        value: this.specVisible ? this.sellingPrice : ""
      },
    ];
  }
  

  handleUpdate(event: any): void {
    this.upperFormFields = event.fields;
    const changedField = event.fields[event.updatedValue];

    if (changedField?.id === 'model') {
      this.selectedModelCode = changedField.value || '';
      const selectedModel = this.models.find((model: any) => model.value === this.selectedModelCode);
      this.selectedModelName = selectedModel ? selectedModel.display : '';
      this.selectedColour = '';
      this.colours = [];
      this.sellingPrice = '';
      this.standardSpecification = [];
      this.standardAccessories = [];
      this.optionalAccessories = [];
      this.filteredData = [];
      this.specVisible = false;
      this.stockRequest?.unsubscribe();
      this.tableVisible = false;
      this.updateUpperFormFields();

      if (!this.selectedModelCode) {
        return;
      }

      this.getColoursByModel(this.selectedModelCode);
      this.getVehicleStockAvailability(this.selectedModelName);
      return;
    }

    if (changedField?.id === 'right') {
      this.selectedColour = changedField.value || '';
      this.specVisible = Boolean(this.selectedModelCode && this.selectedColour);
      this.updateUpperFormFields();

      if (this.specVisible) {
        this.getVehicleDetails(this.selectedModelCode, this.selectedColour);
      }
    }
  }

  getColoursByModel(modelCode: string, initialColour: string = ''): void {
    this.vehicleSelectionService.getColourDrpList(modelCode).subscribe(
      (response) => {
        if (Array.isArray(response?.body)) {
          this.colours = response.body.map((colour: any) => ({
            value: `${colour.Value}`,
            display: `${colour.Value} - ${colour.Name}`,
          }));

          if (initialColour) {
            this.selectedColour = initialColour;
            this.specVisible = true;
            this.getVehicleDetails(this.selectedModelCode, this.selectedColour);
          }

          this.updateUpperFormFields();
        } else {
          this.notifier.notify('error', response?.message || 'Error loading colours.');
        }
      },
      (error) => {
        console.error("Error obteniendo colores:", error);
        this.notifier.notify('error', 'Error loading colours.');
      }
    );
  }

  getVehicleStockAvailability(modelDesc: string): void {
    this.stockRequest?.unsubscribe();
    // Data Table captures its headers on initialization. Recreate it only
    // after the response provides the complete set of monthly columns.
    this.tableVisible = false;
    this.filteredData = [];
    const formattedModelDesc = encodeURIComponent(JSON.stringify(modelDesc));

    this.stockRequest = this.vehicleSelectionService.getVehicleStockAvailability(formattedModelDesc).subscribe(
      (response) => {
        if (response?.statusCode === 200 && Array.isArray(response.body?.GridData) && Array.isArray(response.body?.ExtraData)) {
          const gridData = response.body.GridData;
          const extraData = response.body.ExtraData;

          this.filteredData = gridData.map((item: any) => {
            const row: any = {
              "extColour": `${item.ExtColourCode} - ${item.ExtColourDescription}`,
              "type": item.Type,
              "intColour": `${item.IntColourCode} - ${item.IntColourDescription}`,
              "inStock": item.InStock,
            };

            extraData.forEach((extra: any, index: number) => {
              const matchingItems = Array.isArray(extra?.ItemList)
                ? extra.ItemList.filter((stock: any) =>
                    this.matchesColour(stock.ExtColourCode, item.ExtColourCode, item.ExtColourDescription) &&
                    this.matchesColour(stock.IntColourCode, item.IntColourCode, item.IntColourDescription),
                  )
                : [];
              row[`eta_${index}`] = matchingItems.reduce(
                (quantity: number, stock: any) => quantity + Number(stock.Quantity || 0),
                0,
              );
            });
            return row;
          });

          this.tableHeaders_vehicleStock = this.generateVehicleStockHeaders(extraData);
          this.tableVisible = true;
        } else {
          this.filteredData = [];
          this.tableHeaders_vehicleStock = this.generateVehicleStockHeaders();
          this.notifier.notify('error', response?.message || 'Error loading vehicle stock availability.');
        }
      },
      (error) => {
        console.error("Error obteniendo disponibilidad de stock:", error);
        this.notifier.notify('error', 'Error loading vehicle stock availability.');
      }
    );
  }

  private stockMonthInEnglish(month: { MonthName: string }): string {
    const prefix = String(month.MonthName ?? "").trim().toUpperCase().slice(0, 3);
    const spanishMonths = [
      "ENE", "FEB", "MAR", "ABR", "MAY", "JUN",
      "JUL", "AGO", "SEP", "OCT", "NOV", "DIC",
    ];
    const englishMonths = [
      "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
      "JUL", "AUG", "SEP", "OCT", "NOV", "DEC",
    ];
    const index = spanishMonths.indexOf(prefix === "SET" ? "SEP" : prefix);
    return index >= 0 ? englishMonths[index] : prefix;
  }

  ngOnDestroy(): void {
    this.stockRequest?.unsubscribe();
  }

  generateVehicleStockHeaders(extraData: any[] = []): any[] {
    const headers: any[] = [
      { title: "EXTERIOR COLOUR", type: "data", id: "extColour" },
      { title: "TYPE", type: "data", id: "type" },
      { title: "INT COLOUR", type: "data", id: "intColour" },
      { title: "IN STOCK", type: "data", id: "inStock" }
    ];

    extraData.forEach((month, index) => {
      headers.push({
        title: `ETA (MID ${this.stockMonthInEnglish(month)})`,
        type: "data",
        id: `eta_${index}`
      });
    });

    return headers;
  }

  private matchesColour(value: any, code: any, description: any): boolean {
    const stockValue = String(value ?? '').trim();
    const colourCode = String(code ?? '').trim();
    const colourDescription = String(description ?? '').trim();
    return stockValue === colourCode || stockValue === `${colourCode} - ${colourDescription}`;
  }

  getVehicleDetails(modelCode: string, colourCode: string): void {
    this.vehicleSelectionService.getAllVehicles(colourCode, modelCode).subscribe(
      (response) => {
        if (response?.code === 0 && response.body) {
          const vehicleData = response.body;
  
          this.sellingPrice = vehicleData.SellingPrice ? vehicleData.SellingPrice : "N/A";
          
          this.updateUpperFormFields(); 
  
          this.standardSpecification = this.mapSpecificationData(vehicleData.Specification || {});
  
          const standardAccessories = Array.isArray(vehicleData.StandardAccessories)
            ? vehicleData.StandardAccessories
            : [];
          const optionalAccessories = Array.isArray(vehicleData.OptionalAccessories)
            ? vehicleData.OptionalAccessories
            : [];

          this.standardAccessories = standardAccessories.map((item: any) => ({
            item: item.Name || "N/A"
          }));
  
          this.optionalAccessories = optionalAccessories.map((item: any) => ({
            item: item.Name || "N/A", 
            price: item.Value || "N/A"
          }));
        } else {
          this.notifier.notify('error', response?.message || 'Error loading vehicle details.');
        }
      },
      (error) => {
        console.error("Error al obtener los detalles del vehículo:", error);
        this.notifier.notify('error', 'Error loading vehicle details.');
      }
    );
  }
  
  mapSpecificationData(specification: any): FormField[] {
    return [
      { type: "const", id: "engineType", label: "Engine Type", value: specification.EngineType || "" },
      { type: "const", id: "displacement", label: "Displacement (cc)", value: specification.Displacement || "" },
      { type: "const", id: "boreStroke", label: "Bore x Stroke (mm)", value: specification.BoreStroke || "" },
      { type: "const", id: "compressionRatio", label: "Compression Ratio", value: specification.CompressionRatio || "" },
      { type: "const", id: "maxPower", label: "Max Power (kW/rpm)", value: specification.MaxPower || "" },
      { type: "const", id: "maxTorque", label: "Max Torque (N.m/rpm)", value: specification.MaxTorque || "" },
      { type: "const", id: "lubricantCap", label: "Lubricant Capacity (litre)", value: specification.LubricantCap || "" },
      { type: "const", id: "steeringWheelType", label: "Steering Wheel Type", value: specification.SteeringWheelType || "" },
      { type: "const", id: "steeringGearRatio", label: "Overall Steering Gear Ratio", value: specification.SteeringGearRatio || "" },
      { type: "const", id: "steeringWheelTurn", label: "Number of Steering Wheel Turn", value: specification.SteeringWheelTurn || "" },
      { type: "const", id: "turningRadius", label: "Turning Radius at Wheel (m)", value: specification.TurningRadius || "" },
      { type: "const", id: "susFront", label: "Suspension Front", value: specification.SusFront || "" },
      { type: "const", id: "susRear", label: "Suspension Rear", value: specification.SusRear || "" },
      { type: "const", id: "shockAbsorber", label: "Shock Absorber", value: specification.ShockAbsorber || "" },
      { type: "const", id: "brakesGrl", label: "General Brakes", value: specification.BrakesGrl || "" },
      { type: "const", id: "frontBrake", label: "Front Brake Type", value: specification.FrontBrake || "" },
      { type: "const", id: "rearBrake", label: "Rear Brake Type", value: specification.RearBrake || "" },
      { type: "const", id: "booster", label: "Booster Size/Type", value: specification.Booster || "" },
      { type: "const", id: "length", label: "Length (mm)", value: specification.Length || "" },
      { type: "const", id: "width", label: "Width (mm)", value: specification.Width || "" },
      { type: "const", id: "height", label: "Height (mm)", value: specification.Height || "" },
      { type: "const", id: "wheelBase", label: "WheelBase (mm)", value: specification.WheelBase || "" },
    ];
  }
  
  goToBooking(): void {
    if (!this.selectedModelName || !this.selectedColour) {
      this.notifier.notify('warning', 'Please select a model and colour.');
      return;
    }

    this.router.navigate(["../booking"], {
      relativeTo: this.route,
      state: {
        model: this.selectedModelName,
        colour: this.selectedColour
      }
    });
  }
}

